/**
 * Éditeur du recto d'une SKILLCARD : une liste de sections structurées
 * (étapes, formule, tableau, texte, exemple, quand l'utiliser, piège, figure).
 *
 * Chaque champ garde son texte localement (on peut taper une ligne vide sans
 * qu'elle disparaisse) et remonte la section reconstruite à chaque frappe. La
 * validation finale est celle du schéma partagé `cardContentSchema` (mêmes
 * règles que la génération IA) : le parent n'enregistre qu'un contenu valide.
 * Remonter l'éditeur (prop `key`) pour repartir d'un contenu régénéré.
 */

import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  CARD_LIMITS,
  CARD_SECTION_KINDS,
  CARD_SECTION_LABELS,
  cardSectionSchema,
  emptySection,
  type CardContent,
  type CardSection,
  type CardSectionKind,
} from '@jeraisonne/db/cards';

const inputClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none';

/** « a | b | c » → cellules ; tolère les espaces autour du séparateur. */
export function parseTableText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.split('|').map((c) => c.trim()));
  return { headers: lines[0] ?? [], rows: lines.slice(1) };
}

export function tableToText(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((r) => r.join(' | ')).join('\n');
}

export function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/** « c : hypoténuse » par ligne → variables. */
export function parseVariables(text: string): { symbol: string; meaning: string }[] {
  return splitLines(text)
    .map((line) => {
      const idx = line.indexOf(':');
      if (idx < 0) return { symbol: line, meaning: '' };
      return { symbol: line.slice(0, idx).trim(), meaning: line.slice(idx + 1).trim() };
    })
    .filter((v) => v.symbol);
}

function Field({
  label,
  value,
  onChange,
  multiline,
  rows = 3,
  maxLength,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
  hint?: string;
}) {
  const [text, setText] = useState(value);
  const update = (v: string) => {
    setText(v);
    onChange(v);
  };
  return (
    <label className="block space-y-1">
      <span className="text-ink-600 text-xs font-semibold">{label}</span>
      {multiline ? (
        <textarea
          className={`${inputClass} font-mono text-[13px]`}
          rows={rows}
          value={text}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => update(e.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          value={text}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => update(e.target.value)}
        />
      )}
      {hint && <span className="text-ink-400 block text-xs">{hint}</span>}
    </label>
  );
}

function SectionFields({
  section,
  onChange,
}: {
  section: CardSection;
  onChange: (next: CardSection | { invalid: string }) => void;
}) {
  const L = CARD_LIMITS;
  switch (section.kind) {
    case 'steps':
      return (
        <div className="space-y-2">
          <Field
            label="Titre (optionnel)"
            value={section.title ?? ''}
            maxLength={L.title}
            onChange={(v) => onChange({ ...section, title: v || undefined })}
          />
          <Field
            label="Étapes — une par ligne"
            multiline
            rows={5}
            value={section.items.join('\n')}
            onChange={(v) => onChange({ ...section, items: splitLines(v) })}
            hint="Maths entre $…$, ex. $A\times B=0$"
          />
        </div>
      );
    case 'formula':
      return (
        <div className="space-y-2">
          <Field
            label="Titre (optionnel)"
            value={section.title ?? ''}
            maxLength={L.title}
            onChange={(v) => onChange({ ...section, title: v || undefined })}
          />
          <Field
            label="Formule (LaTeX, sans $)"
            value={section.latex}
            maxLength={L.latex}
            onChange={(v) => onChange({ ...section, latex: v })}
          />
          <Field
            label="Variables — « symbole : signification » par ligne"
            multiline
            rows={3}
            value={section.variables.map((v) => `${v.symbol} : ${v.meaning}`).join('\n')}
            onChange={(v) => onChange({ ...section, variables: parseVariables(v) })}
          />
          <Field
            label="Conditions (optionnel)"
            value={section.conditions ?? ''}
            maxLength={L.conditions}
            onChange={(v) => onChange({ ...section, conditions: v || undefined })}
          />
        </div>
      );
    case 'table':
      return (
        <div className="space-y-2">
          <Field
            label="Titre (optionnel)"
            value={section.title ?? ''}
            maxLength={L.title}
            onChange={(v) => onChange({ ...section, title: v || undefined })}
          />
          <Field
            label="Tableau — cellules séparées par « | », 1re ligne = en-têtes"
            multiline
            rows={5}
            value={tableToText(section.headers, section.rows)}
            onChange={(v) => onChange({ ...section, ...parseTableText(v) })}
            hint={`${L.tableColumns} colonnes et ${L.tableRows} lignes au maximum.`}
          />
        </div>
      );
    case 'markdown':
      return (
        <div className="space-y-2">
          <Field
            label="Titre (optionnel)"
            value={section.title ?? ''}
            maxLength={L.title}
            onChange={(v) => onChange({ ...section, title: v || undefined })}
          />
          <Field
            label="Texte"
            multiline
            rows={4}
            value={section.md}
            maxLength={L.markdown}
            onChange={(v) => onChange({ ...section, md: v })}
          />
        </div>
      );
    case 'example':
      return (
        <div className="space-y-2">
          <Field
            label="Énoncé de l’exemple"
            value={section.statement}
            maxLength={L.exampleStatement}
            onChange={(v) => onChange({ ...section, statement: v })}
          />
          <Field
            label="Étapes — une par ligne (optionnel)"
            multiline
            rows={3}
            value={section.steps.join('\n')}
            onChange={(v) => onChange({ ...section, steps: splitLines(v) })}
          />
          <Field
            label="Résultat (optionnel)"
            value={section.result ?? ''}
            maxLength={L.exampleResult}
            onChange={(v) => onChange({ ...section, result: v || undefined })}
          />
        </div>
      );
    case 'when_to_use':
    case 'pitfall':
      return (
        <Field
          label="Texte"
          multiline
          rows={3}
          value={section.md}
          maxLength={L.shortText}
          onChange={(v) => onChange({ ...section, md: v })}
        />
      );
    case 'visual':
      return (
        <div className="space-y-2">
          <Field
            label="Légende (optionnel)"
            value={section.caption ?? ''}
            maxLength={L.caption}
            onChange={(v) => onChange({ ...section, caption: v || undefined })}
          />
          <Field
            label="Figure (JSON du bloc visuel)"
            multiline
            rows={8}
            value={JSON.stringify(section.block, null, 2)}
            onChange={(v) => {
              try {
                onChange({ ...section, block: JSON.parse(v) });
              } catch {
                onChange({ invalid: 'JSON de la figure invalide.' });
              }
            }}
          />
        </div>
      );
  }
}

export interface CardSectionsEditorProps {
  content: CardContent;
  onChange: (content: CardContent, valid: boolean) => void;
}

export function CardSectionsEditor({ content, onChange }: CardSectionsEditorProps) {
  const [sections, setSections] = useState<CardSection[]>(content.sections);
  // Clé stable par section (réordonner ne doit pas mélanger les champs locaux).
  const [keys, setKeys] = useState<number[]>(() => content.sections.map((_, i) => i));
  const [nextKey, setNextKey] = useState(content.sections.length);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [newKind, setNewKind] = useState<CardSectionKind>('steps');

  const emit = (next: CardSection[], nextErrors: Record<number, string>) => {
    setSections(next);
    setErrors(nextErrors);
    onChange({ sections: next }, Object.keys(nextErrors).length === 0);
  };

  const updateAt = (index: number, value: CardSection | { invalid: string }) => {
    const key = keys[index] as number;
    const nextErrors = { ...errors };
    if ('invalid' in value) {
      nextErrors[key] = value.invalid;
      emit(sections, nextErrors);
      return;
    }
    const parsed = cardSectionSchema.safeParse(value);
    if (parsed.success) delete nextErrors[key];
    else nextErrors[key] = parsed.error.issues[0]?.message ?? 'Section invalide.';
    const next = sections.map((s, i) => (i === index ? value : s));
    emit(next, nextErrors);
  };

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    const nextKeys = [...keys];
    [next[index], next[target]] = [next[target] as CardSection, next[index] as CardSection];
    [nextKeys[index], nextKeys[target]] = [nextKeys[target] as number, nextKeys[index] as number];
    setKeys(nextKeys);
    emit(next, errors);
  };

  const remove = (index: number) => {
    const key = keys[index] as number;
    const nextErrors = { ...errors };
    delete nextErrors[key];
    setKeys(keys.filter((_, i) => i !== index));
    emit(
      sections.filter((_, i) => i !== index),
      nextErrors,
    );
  };

  const add = () => {
    if (sections.length >= CARD_LIMITS.sections) return;
    setKeys([...keys, nextKey]);
    setNextKey(nextKey + 1);
    emit([...sections, emptySection(newKind)], errors);
  };

  return (
    <div className="space-y-3">
      {sections.map((section, i) => {
        const key = keys[i] as number;
        return (
          <div key={key} className="border-ink-200 space-y-3 rounded-xl border bg-white p-3">
            <div className="flex items-center gap-1">
              <span className="text-ink-900 flex-1 text-sm font-semibold">
                {i + 1}. {CARD_SECTION_LABELS[section.kind]}
              </span>
              <button
                type="button"
                aria-label="Monter la section"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                className="text-ink-500 hover:bg-ink-50 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-lg disabled:opacity-30"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Descendre la section"
                disabled={i === sections.length - 1}
                onClick={() => move(i, 1)}
                className="text-ink-500 hover:bg-ink-50 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-lg disabled:opacity-30"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Supprimer la section"
                onClick={() => remove(i)}
                className="text-accent-600 hover:bg-accent-50 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-lg"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <SectionFields section={section} onChange={(v) => updateAt(i, v)} />
            {errors[key] && (
              <p role="alert" className="text-accent-600 text-xs">
                {errors[key]}
              </p>
            )}
          </div>
        );
      })}

      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          aria-label="Type de section à ajouter"
          className={`${inputClass} min-h-[2.75rem] sm:w-auto`}
          value={newKind}
          onChange={(e) => setNewKind(e.target.value as CardSectionKind)}
        >
          {CARD_SECTION_KINDS.map((k) => (
            <option key={k} value={k}>
              {CARD_SECTION_LABELS[k]}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          onClick={add}
          disabled={sections.length >= CARD_LIMITS.sections}
        >
          <span className="flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Ajouter une section
          </span>
        </Button>
      </div>
    </div>
  );
}
